import React, { FC, Fragment } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { Invoice, InvoiceStatus, InvoicingType } from 'models/invoice';

type InvoiceItemNumberStatusProps = {
    invoiceNumber: string;
    pdfUrl: string;
    status: string;
    invoiceType?: string;
    isCredited?: boolean;
    creditInvoices?: Invoice[];
};

const InvoiceItemNumberStatus: FC<InvoiceItemNumberStatusProps> = ({
    invoiceNumber,
    pdfUrl,
    status,
    invoiceType,
    isCredited,
    creditInvoices = [],
}: InvoiceItemNumberStatusProps) => {
    const { t: translate } = useTranslation(['invoices']);

    const colorMap = {
        [InvoiceStatus.ALL.valueOf()]: '', // Ignore this, just to solve type issues
        [InvoiceStatus.CREATED.valueOf()]: '#444',
        [InvoiceStatus.SCHEDULED.valueOf()]: 'warning.main',
        [InvoiceStatus.PROCESSING.valueOf()]: 'warning.main',
        [InvoiceStatus.SENT.valueOf()]: 'success.main',
        [InvoiceStatus.ERROR.valueOf()]: 'error.main',
        [InvoiceStatus.EXTERNAL_INVOICE_ERROR.valueOf()]: 'warning.main',
        [InvoiceStatus.VOIDED.valueOf()]: 'error.main',
    };

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
                {invoiceType === InvoicingType.COLLECTIVE ? undefined : <Box>{invoiceNumber}</Box>}

                {isCredited ? (
                    <Fragment>
                        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: '700' }}>
                            {translate('CREDITED')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            {translate('CREDIT_INVOICE')}:{' '}
                            {creditInvoices.map((creditInvoice) => creditInvoice.invoiceNumber)}
                        </Typography>
                    </Fragment>
                ) : (
                    <Typography variant="body2" sx={{ color: colorMap[status] }}>
                        {translate(`${status}`)}
                    </Typography>
                )}
            </Box>
            {pdfUrl ? (
                <Box style={{ marginLeft: 5 }}>
                    <Tooltip title={translate('INVOICE_PDF').toString()}>
                        <IconButton
                            disabled={status === InvoiceStatus.ERROR}
                            href={pdfUrl}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            size="small"
                            aria-label="delete"
                        >
                            <DescriptionIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : undefined}
        </Box>
    );
};

export default InvoiceItemNumberStatus;
